import type {DcdOwnershipBreadcrumbType} from 'analytics/dcdOwnershipAnalytics';
import {logDcdOwnershipDetailInteraction} from 'analytics/dcdOwnershipAnalytics';
import {useDcdOwnershipSearchAnalytics} from 'analytics/useDcdOwnershipSearchAnalytics';
import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {useDcdOwnershipService, useDcdOwnershipServices} from 'components/teams/dcdOwnership';
import {DcdOwnershipAppDetail} from 'components/teams/DcdOwnershipAppDetail';
import {DcdOwnershipServiceList} from 'components/teams/DcdOwnershipServiceList';
import {parseIdParam, useDcdOwnershipPageType} from 'components/teams/ownershipHelpers';
import {TeamOwnershipDirectoryLayout} from 'components/teams/TeamOwnershipDirectoryLayout';
import {getDefaultTeamsRoute, getTeamsAppsRoute} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router';

export const TeamAppsView = () => {
  const {serviceId: serviceIdParam} = useParams<{serviceId?: string}>();
  const pageType = useDcdOwnershipPageType();
  const serviceId = useMemo(() => parseIdParam(serviceIdParam), [serviceIdParam]);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const {data: services = [], isLoading, isError} = useDcdOwnershipServices();
  const selectedService = useMemo(
    () => services.find(({id}) => id === serviceId),
    [serviceId, services]
  );
  const {
    data: selectedApp,
    isLoading: isAppLoading,
    isError: isAppError,
  } = useDcdOwnershipService(selectedService?.id);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleServices = useMemo(
    () =>
      normalizedSearch
        ? services.filter(({name}) => name.toLocaleLowerCase().includes(normalizedSearch))
        : services,
    [normalizedSearch, services]
  );
  const {recordSelection} = useDcdOwnershipSearchAnalytics({
    query: search,
    searchSurface: 'apps',
    useServerSearch: false,
    resultCount: visibleServices.length,
    isLoading,
    isError,
  });
  const trackBreadcrumbClick = (breadcrumbType: DcdOwnershipBreadcrumbType) => () => {
    if (pageType) {
      logDcdOwnershipDetailInteraction('breadcrumb_clicked', {breadcrumbType, pageType});
    }
  };
  useDocumentTitle(selectedService?.name ?? t('teams_apps').toString());

  useEffect(() => {
    if (isLoading || isError || !services.length || selectedService) {
      return;
    }

    navigate(getTeamsAppsRoute(services[0].id), {replace: true});
  }, [isError, isLoading, navigate, selectedService, services]);

  return (
    <TeamOwnershipDirectoryLayout
      activeSection="apps"
      search={search}
      searchPlaceholder={t('teams_apps_search').toString()}
      onSearchChange={setSearch}
      isDirectoryEmpty={!visibleServices.length}
      isDirectoryError={isError}
      isDirectoryLoading={isLoading}
      detailLoadingVariant={selectedService && isAppLoading ? 'detail' : undefined}
      isDetailError={isAppError}
      directory={(bleed) => (
        <DcdOwnershipServiceList
          bleed={bleed}
          services={visibleServices}
          activeServiceId={serviceId}
          onSelect={(rank) => recordSelection({resultType: 'app', rank})}
          onNavigate={() => setSearch('')}
        />
      )}
      breadcrumb={
        <Breadcrumb
          path={[
            {
              children: t('teams'),
              to: getDefaultTeamsRoute(),
              onClick: trackBreadcrumbClick('teams'),
            },
            {
              children: t('teams_apps'),
              to: getTeamsAppsRoute(),
              onClick: trackBreadcrumbClick('apps'),
            },
            ...(selectedService
              ? [
                  {
                    children: selectedService.name,
                    to: getTeamsAppsRoute(selectedService.id),
                    onClick: trackBreadcrumbClick('app'),
                  },
                ]
              : []),
          ]}
        />
      }
    >
      {selectedApp ? <DcdOwnershipAppDetail app={selectedApp} /> : null}
    </TeamOwnershipDirectoryLayout>
  );
};
